import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, Select, MenuItem, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import PageHelmet from '../SEO/PageHelmet';
import { apiService } from '../../services/api';
import YovoTripHeader from '../Header/YovoTripHeader';
import MobileBottomNav from '../Homepage/MobileBottomNav';
import MobileChatbotOverlay from '../Homepage/MobileChatbotOverlay';
import RegionalSettingsModal from '../Homepage/RegionalSettingsModal';
import LoginModal from './LoginModal';
import SignupModal from './SignupModal';

const AccountPage = () => {
  const { user, logout, isAuthenticated, loading: authInitializing } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  /** Figma profile mobile (2349:51520): full-bleed screen, no maroon homepage header */
  const isMobileProfileLayout = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [regionalSettingsOpen, setRegionalSettingsOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({ code: '+91', name: 'India', flag: '🇮🇳' });

  // Country options for the dropdown
  const countries = [
    { code: '+91', name: 'India', flag: '🇮🇳' },
    { code: '+93', name: 'Afghanistan', flag: '🇦🇫' },
    { code: '+355', name: 'Albania', flag: '🇦🇱' },
    { code: '+213', name: 'Algeria', flag: '🇩🇿' },
    { code: '+376', name: 'Andorra', flag: '🇦🇩' },
    { code: '+244', name: 'Angola', flag: '🇦🇴' },
    { code: '+54', name: 'Argentina', flag: '🇦🇷' },
    { code: '+374', name: 'Armenia', flag: '🇦🇲' },
    { code: '+61', name: 'Australia', flag: '🇦🇺' },
    { code: '+43', name: 'Austria', flag: '🇦🇹' },
    { code: '+994', name: 'Azerbaijan', flag: '🇦🇿' },
    { code: '+973', name: 'Bahrain', flag: '🇧🇭' },
    { code: '+880', name: 'Bangladesh', flag: '🇧🇩' },
    { code: '+375', name: 'Belarus', flag: '🇧🇾' },
    { code: '+32', name: 'Belgium', flag: '🇧🇪' },
    { code: '+501', name: 'Belize', flag: '🇧🇿' },
    { code: '+229', name: 'Benin', flag: '🇧🇯' },
    { code: '+975', name: 'Bhutan', flag: '🇧🇹' },
    { code: '+591', name: 'Bolivia', flag: '🇧🇴' },
    { code: '+387', name: 'Bosnia and Herzegovina', flag: '🇧🇦' },
    { code: '+267', name: 'Botswana', flag: '🇧🇼' },
    { code: '+55', name: 'Brazil', flag: '🇧🇷' },
    { code: '+673', name: 'Brunei', flag: '🇧🇳' },
    { code: '+359', name: 'Bulgaria', flag: '🇧🇬' },
    { code: '+226', name: 'Burkina Faso', flag: '🇧🇫' },
    { code: '+257', name: 'Burundi', flag: '🇧🇮' },
    { code: '+855', name: 'Cambodia', flag: '🇰🇭' },
    { code: '+237', name: 'Cameroon', flag: '🇨🇲' },
    { code: '+1', name: 'Canada', flag: '🇨🇦' },
    { code: '+238', name: 'Cape Verde', flag: '🇨🇻' },
    { code: '+236', name: 'Central African Republic', flag: '🇨🇫' },
    { code: '+235', name: 'Chad', flag: '🇹🇩' },
    { code: '+56', name: 'Chile', flag: '🇨🇱' },
    { code: '+86', name: 'China', flag: '🇨🇳' },
    { code: '+57', name: 'Colombia', flag: '🇨🇴' },
    { code: '+269', name: 'Comoros', flag: '🇰🇲' },
    { code: '+243', name: 'Congo (DRC)', flag: '🇨🇩' },
    { code: '+242', name: 'Congo (Republic)', flag: '🇨🇬' },
    { code: '+506', name: 'Costa Rica', flag: '🇨🇷' },
    { code: '+385', name: 'Croatia', flag: '🇭🇷' },
    { code: '+53', name: 'Cuba', flag: '🇨🇺' },
    { code: '+357', name: 'Cyprus', flag: '🇨🇾' },
    { code: '+420', name: 'Czech Republic', flag: '🇨🇿' },
    { code: '+45', name: 'Denmark', flag: '🇩🇰' },
    { code: '+253', name: 'Djibouti', flag: '🇩🇯' },
    { code: '+593', name: 'Ecuador', flag: '🇪🇨' },
    { code: '+20', name: 'Egypt', flag: '🇪🇬' },
    { code: '+503', name: 'El Salvador', flag: '🇸🇻' },
    { code: '+240', name: 'Equatorial Guinea', flag: '🇬🇶' },
    { code: '+291', name: 'Eritrea', flag: '🇪🇷' },
    { code: '+372', name: 'Estonia', flag: '🇪🇪' },
    { code: '+251', name: 'Ethiopia', flag: '🇪🇹' },
    { code: '+679', name: 'Fiji', flag: '🇫🇯' },
    { code: '+358', name: 'Finland', flag: '🇫🇮' },
    { code: '+33', name: 'France', flag: '🇫🇷' },
    { code: '+241', name: 'Gabon', flag: '🇬🇦' },
    { code: '+220', name: 'Gambia', flag: '🇬🇲' },
    { code: '+995', name: 'Georgia', flag: '🇬🇪' },
    { code: '+49', name: 'Germany', flag: '🇩🇪' },
    { code: '+233', name: 'Ghana', flag: '🇬🇭' },
    { code: '+30', name: 'Greece', flag: '🇬🇷' },
    { code: '+502', name: 'Guatemala', flag: '🇬🇹' },
    { code: '+224', name: 'Guinea', flag: '🇬🇳' },
    { code: '+592', name: 'Guyana', flag: '🇬🇾' },
    { code: '+509', name: 'Haiti', flag: '🇭🇹' },
    { code: '+504', name: 'Honduras', flag: '🇭🇳' },
    { code: '+852', name: 'Hong Kong', flag: '🇭🇰' },
    { code: '+36', name: 'Hungary', flag: '🇭🇺' },
    { code: '+354', name: 'Iceland', flag: '🇮🇸' },
    { code: '+62', name: 'Indonesia', flag: '🇮🇩' },
    { code: '+98', name: 'Iran', flag: '🇮🇷' },
    { code: '+964', name: 'Iraq', flag: '🇮🇶' },
    { code: '+353', name: 'Ireland', flag: '🇮🇪' },
    { code: '+972', name: 'Israel', flag: '🇮🇱' },
    { code: '+39', name: 'Italy', flag: '🇮🇹' },
    { code: '+225', name: 'Ivory Coast', flag: '🇨🇮' },
    { code: '+1876', name: 'Jamaica', flag: '🇯🇲' },
    { code: '+81', name: 'Japan', flag: '🇯🇵' },
    { code: '+962', name: 'Jordan', flag: '🇯🇴' },
    { code: '+7', name: 'Kazakhstan', flag: '🇰🇿' },
    { code: '+254', name: 'Kenya', flag: '🇰🇪' },
    { code: '+965', name: 'Kuwait', flag: '🇰🇼' },
    { code: '+996', name: 'Kyrgyzstan', flag: '🇰🇬' },
    { code: '+856', name: 'Laos', flag: '🇱🇦' },
    { code: '+371', name: 'Latvia', flag: '🇱🇻' },
    { code: '+961', name: 'Lebanon', flag: '🇱🇧' },
    { code: '+266', name: 'Lesotho', flag: '🇱🇸' },
    { code: '+231', name: 'Liberia', flag: '🇱🇷' },
    { code: '+218', name: 'Libya', flag: '🇱🇾' },
    { code: '+423', name: 'Liechtenstein', flag: '🇱🇮' },
    { code: '+370', name: 'Lithuania', flag: '🇱🇹' },
    { code: '+352', name: 'Luxembourg', flag: '🇱🇺' },
    { code: '+853', name: 'Macau', flag: '🇲🇴' },
    { code: '+261', name: 'Madagascar', flag: '🇲🇬' },
    { code: '+265', name: 'Malawi', flag: '🇲🇼' },
    { code: '+60', name: 'Malaysia', flag: '🇲🇾' },
    { code: '+960', name: 'Maldives', flag: '🇲🇻' },
    { code: '+223', name: 'Mali', flag: '🇲🇱' },
    { code: '+356', name: 'Malta', flag: '🇲🇹' },
    { code: '+222', name: 'Mauritania', flag: '🇲🇷' },
    { code: '+230', name: 'Mauritius', flag: '🇲🇺' },
    { code: '+52', name: 'Mexico', flag: '🇲🇽' },
    { code: '+373', name: 'Moldova', flag: '🇲🇩' },
    { code: '+377', name: 'Monaco', flag: '🇲🇨' },
    { code: '+976', name: 'Mongolia', flag: '🇲🇳' },
    { code: '+382', name: 'Montenegro', flag: '🇲🇪' },
    { code: '+212', name: 'Morocco', flag: '🇲🇦' },
    { code: '+258', name: 'Mozambique', flag: '🇲🇿' },
    { code: '+95', name: 'Myanmar', flag: '🇲🇲' },
    { code: '+264', name: 'Namibia', flag: '🇳🇦' },
    { code: '+977', name: 'Nepal', flag: '🇳🇵' },
    { code: '+31', name: 'Netherlands', flag: '🇳🇱' },
    { code: '+64', name: 'New Zealand', flag: '🇳🇿' },
    { code: '+505', name: 'Nicaragua', flag: '🇳🇮' },
    { code: '+227', name: 'Niger', flag: '🇳🇪' },
    { code: '+234', name: 'Nigeria', flag: '🇳🇬' },
    { code: '+850', name: 'North Korea', flag: '🇰🇵' },
    { code: '+389', name: 'North Macedonia', flag: '🇲🇰' },
    { code: '+47', name: 'Norway', flag: '🇳🇴' },
    { code: '+968', name: 'Oman', flag: '🇴🇲' },
    { code: '+92', name: 'Pakistan', flag: '🇵🇰' },
    { code: '+970', name: 'Palestine', flag: '🇵🇸' },
    { code: '+507', name: 'Panama', flag: '🇵🇦' },
    { code: '+675', name: 'Papua New Guinea', flag: '🇵🇬' },
    { code: '+595', name: 'Paraguay', flag: '🇵🇾' },
    { code: '+51', name: 'Peru', flag: '🇵🇪' },
    { code: '+63', name: 'Philippines', flag: '🇵🇭' },
    { code: '+48', name: 'Poland', flag: '🇵🇱' },
    { code: '+351', name: 'Portugal', flag: '🇵🇹' },
    { code: '+974', name: 'Qatar', flag: '🇶🇦' },
    { code: '+40', name: 'Romania', flag: '🇷🇴' },
    { code: '+7', name: 'Russia', flag: '🇷🇺' },
    { code: '+250', name: 'Rwanda', flag: '🇷🇼' },
    { code: '+966', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+221', name: 'Senegal', flag: '🇸🇳' },
    { code: '+381', name: 'Serbia', flag: '🇷🇸' },
    { code: '+248', name: 'Seychelles', flag: '🇸🇨' },
    { code: '+232', name: 'Sierra Leone', flag: '🇸🇱' },
    { code: '+65', name: 'Singapore', flag: '🇸🇬' },
    { code: '+421', name: 'Slovakia', flag: '🇸🇰' },
    { code: '+386', name: 'Slovenia', flag: '🇸🇮' },
    { code: '+252', name: 'Somalia', flag: '🇸🇴' },
    { code: '+27', name: 'South Africa', flag: '🇿🇦' },
    { code: '+82', name: 'South Korea', flag: '🇰🇷' },
    { code: '+211', name: 'South Sudan', flag: '🇸🇸' },
    { code: '+34', name: 'Spain', flag: '🇪🇸' },
    { code: '+94', name: 'Sri Lanka', flag: '🇱🇰' },
    { code: '+249', name: 'Sudan', flag: '🇸🇩' },
    { code: '+597', name: 'Suriname', flag: '🇸🇷' },
    { code: '+46', name: 'Sweden', flag: '🇸🇪' },
    { code: '+41', name: 'Switzerland', flag: '🇨🇭' },
    { code: '+963', name: 'Syria', flag: '🇸🇾' },
    { code: '+886', name: 'Taiwan', flag: '🇹🇼' },
    { code: '+992', name: 'Tajikistan', flag: '🇹🇯' },
    { code: '+255', name: 'Tanzania', flag: '🇹🇿' },
    { code: '+66', name: 'Thailand', flag: '🇹🇭' },
    { code: '+228', name: 'Togo', flag: '🇹🇬' },
    { code: '+676', name: 'Tonga', flag: '🇹🇴' },
    { code: '+1868', name: 'Trinidad and Tobago', flag: '🇹🇹' },
    { code: '+216', name: 'Tunisia', flag: '🇹🇳' },
    { code: '+90', name: 'Turkey', flag: '🇹🇷' },
    { code: '+993', name: 'Turkmenistan', flag: '🇹🇲' },
    { code: '+256', name: 'Uganda', flag: '🇺🇬' },
    { code: '+380', name: 'Ukraine', flag: '🇺🇦' },
    { code: '+971', name: 'United Arab Emirates', flag: '🇦🇪' },
    { code: '+44', name: 'United Kingdom', flag: '🇬🇧' },
    { code: '+1', name: 'United States', flag: '🇺🇸' },
    { code: '+598', name: 'Uruguay', flag: '🇺🇾' },
    { code: '+998', name: 'Uzbekistan', flag: '🇺🇿' },
    { code: '+58', name: 'Venezuela', flag: '🇻🇪' },
    { code: '+84', name: 'Vietnam', flag: '🇻🇳' },
    { code: '+967', name: 'Yemen', flag: '🇾🇪' },
    { code: '+260', name: 'Zambia', flag: '🇿🇲' },
    { code: '+263', name: 'Zimbabwe', flag: '🇿🇼' },
  ];

  // Populate form with user data when component mounts or user data changes
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        phoneNumber: user.phone_number || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!user?.id) {
      setError('User ID not found. Please try logging in again.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const updateData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone_number: formData.phoneNumber,
        email: formData.email
      };

      await apiService.updateUser(user.id, updateData);

      // Show success message
      setSuccess('Account details updated successfully! Changes will be reflected on your next login.');
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update account details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteAccount = () => {
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteAccount = async () => {
    setDeleteConfirmOpen(false);
    if (!user?.id) {
      setError('Unable to deactivate account right now. Please log in again and retry.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      await apiService.deleteUser(user.id);
      logout();
      navigate('/');
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err.response?.data?.error || err.message || 'Failed to deactivate account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const mobilePhoneLine = [selectedCountry.code, formData.phoneNumber].filter(Boolean).join(' ').trim();

  const handleAssistClick = (initialMessage) => {
    if (initialMessage) window.chatbotInitialMessage = initialMessage;
    setChatbotOpen(true);
  };

  const mobileBottomNav = (
    <MobileBottomNav assistActive={chatbotOpen} onAssistClick={handleAssistClick} />
  );

  const mobileChatbotOverlay = isMobileProfileLayout ? (
    <MobileChatbotOverlay open={chatbotOpen} setOpen={setChatbotOpen} />
  ) : null;

  if (authInitializing) {
    return (
      <>
        <Box
          sx={{
            minHeight: '100dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#F5F5F7',
          }}
        >
          <CircularProgress sx={{ color: '#B22E4C' }} aria-label="Loading" />
        </Box>
        {mobileBottomNav}
        {mobileChatbotOverlay}
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <YovoTripHeader />
        </Box>
        {isMobileProfileLayout && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1100,
              bgcolor: '#FBFBFB',
              borderBottom: '1px solid #E8E9ED',
              pt: 'env(safe-area-inset-top, 0px)',
              pb: '16px',
              px: 2,
              flexShrink: 0,
            }}
          >
            <Typography
              component="h1"
              sx={{
                textAlign: 'center',
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontWeight: 700,
                fontSize: '20px',
                lineHeight: '28px',
                color: '#20202A',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Profile
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: '100dvh', md: '100vh' },
            backgroundColor: isMobileProfileLayout ? '#F5F5F7' : '#FBFBFB',
            pb: { xs: 'calc(72px + env(safe-area-inset-bottom, 0px))', md: 0 },
            px: 2,
            pt: { xs: 3, md: 6 },
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', md: 'center' },
          }}
        >
          <Box
            sx={{
              maxWidth: 420,
              width: '100%',
              mt: { xs: 1, md: 0 },
              p: 3,
              borderRadius: '16px',
              bgcolor: '#FFFFFF',
              boxShadow: { md: '0 2px 12px rgba(0,0,0,0.06)' },
              border: { xs: '1px solid #E8E9ED', md: '1px solid #E8E9ED' },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontWeight: 700,
                fontSize: '22px',
                lineHeight: '28px',
                color: '#20202A',
                mb: 1,
              }}
            >
              Sign in to your account
            </Typography>
            <Typography sx={{ color: '#545665', fontSize: '14px', mb: 3, lineHeight: 1.5 }}>
              Log in to manage your profile, bookings, and preferences.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setLoginModalOpen(true)}
              sx={{
                bgcolor: '#20202A',
                textTransform: 'none',
                py: 1.25,
                mb: 1.5,
                '&:hover': { bgcolor: '#383A47' },
              }}
            >
              Log in
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={() => setSignupModalOpen(true)}
              sx={{ textTransform: 'none', color: '#B22E4C', fontWeight: 600 }}
            >
              Create an account
            </Button>
          </Box>
        </Box>
        <LoginModal
          open={loginModalOpen}
          onClose={() => setLoginModalOpen(false)}
          onSwitchToSignup={() => {
            setLoginModalOpen(false);
            setSignupModalOpen(true);
          }}
        />
        <SignupModal
          open={signupModalOpen}
          onClose={() => setSignupModalOpen(false)}
          onSwitchToLogin={() => {
            setSignupModalOpen(false);
            setLoginModalOpen(true);
          }}
        />
        {mobileBottomNav}
        {mobileChatbotOverlay}
      </>
    );
  }

  return (
    <>
      <PageHelmet title="My Account — Profile & Settings" description="Manage your YovoTrip account, update profile details, and view saved preferences." noIndex />
      <h1 style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>My Account</h1>
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <YovoTripHeader />
      </Box>

      {/* Mobile: Figma 2349:51520 — no search / service tabs; simple title bar only */}
      {isMobileProfileLayout && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            bgcolor: '#FBFBFB',
            borderBottom: '1px solid #E8E9ED',
            pt: 'env(safe-area-inset-top, 0px)',
            pb: '16px',
            px: 2,
            flexShrink: 0,
          }}
        >
          <Typography
            component="h1"
            sx={{
              textAlign: 'center',
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '28px',
              color: '#20202A',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Profile
          </Typography>
        </Box>
      )}

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: { xs: '100dvh', md: '100vh' },
        backgroundColor: isMobileProfileLayout ? '#F5F5F7' : '#FBFBFB',
        pb: { xs: 'calc(72px + env(safe-area-inset-bottom, 0px))', md: 0 },
      }}>
      {/* Red hero — desktop only */}
      <Box sx={{
        display: { xs: 'none', md: 'block' },
        width: '100%',
        height: '186px',
        backgroundColor: '#B22E4C',
        position: 'relative'
      }} />

      {/* Mobile: light card overlaps maroon header (same pattern as Homepage) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: { xs: isMobileProfileLayout ? '100%' : 'calc(100% - 24px)', md: '100%' },
          mx: { xs: isMobileProfileLayout ? 0 : 'auto', md: 0 },
          mt: { xs: isMobileProfileLayout ? 0 : '-80px', md: 0 },
          position: { xs: 'relative', md: 'static' },
          zIndex: { xs: 11, md: 'auto' },
          borderRadius: { xs: isMobileProfileLayout ? 0 : '24px 24px 0 0', md: 0 },
          bgcolor: { xs: isMobileProfileLayout ? 'transparent' : '#F5F5F7', md: 'transparent' },
          pt: { xs: isMobileProfileLayout ? '24px' : '12px', md: 0 },
          overflow: { xs: 'visible', md: 'visible' },
          flex: { md: 1 },
        }}
      >

      {/* Profile Section */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        alignItems: { xs: 'stretch', md: 'center' },
        px: { xs: '16px', sm: '20px', md: '104px' },
        pt: { xs: '16px', md: 0 },
        pb: { xs: '20px', md: '40px' },
        mt: { xs: 0, md: '-120px' },
        position: 'relative',
        zIndex: 1,
        bgcolor: { xs: 'transparent', md: 'transparent' },
        borderRadius: { xs: 0, md: 0 },
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'flex-end' },
          gap: { xs: '16px', md: '24px' },
          flex: '1 0 0',
          position: 'relative',
          width: '100%'
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'row', md: 'row' },
            alignItems: 'center',
            gap: { xs: '16px', md: '24px' },
            flex: { xs: 'none', md: '1 0 0' },
            width: { xs: '100%', md: 'auto' },
            minWidth: 0
          }}>
          {/* Pink Circle Background */}
          <Box sx={{
            width: { xs: isMobileProfileLayout ? '72px' : '104px', sm: '128px', md: '220px' },
            height: { xs: isMobileProfileLayout ? '72px' : '104px', sm: '128px', md: '220px' },
            borderRadius: '50%',
            backgroundColor: '#FFC8D5',
            border: { xs: isMobileProfileLayout ? '3px solid #E8E9ED' : '4px solid #E8E9ED', md: '10px solid #E8E9ED' },
            position: 'relative',
            flexShrink: 0
          }}>
            {/* User Icon */}
            <Box
              component="svg"
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: isMobileProfileLayout ? '42px' : '48px', sm: '56px', md: '96px' },
                height: { xs: isMobileProfileLayout ? '42px' : '48px', sm: '56px', md: '96px' },
              }}
              width="96"
              height="96"
              viewBox="0 0 96 96"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M48 48C59.0457 48 68 39.0457 68 28C68 16.9543 59.0457 8 48 8C36.9543 8 28 16.9543 28 28C28 39.0457 36.9543 48 48 48Z"
                stroke="#B22E4C"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M82.36 88C82.36 72.52 66.96 60 48 60C29.04 60 13.64 72.52 13.64 88"
                stroke="#B22E4C"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Box>

            {/* Edit Icon — commented out until profile photo upload is implemented
            <Box sx={{
              position: 'absolute',
              right: { xs: isMobileProfileLayout ? '0' : '-2px', md: '-4px' },
              bottom: { xs: isMobileProfileLayout ? '4px' : '8px', md: '20px' },
              width: { xs: isMobileProfileLayout ? '24px' : '32px', md: '45px' },
              height: { xs: isMobileProfileLayout ? '24px' : '32px', md: '45px' },
              borderRadius: '50%',
              backgroundColor: '#20202A',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#383A47'
              }
            }}>
              <Box
                component="svg"
                sx={{ width: { xs: 18, md: 24 }, height: { xs: 18, md: 24 } }}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22H15C20 22 22 20 22 15V13"
                  stroke="#FBFBFB"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16.04 3.02123L8.16 10.9012C7.86 11.2012 7.56 11.7912 7.5 12.2212L7.07 15.2312C6.91 16.3212 7.68 17.0812 8.77 16.9312L11.78 16.5012C12.2 16.4412 12.79 16.1412 13.1 15.8412L20.98 7.96123C22.34 6.60123 22.98 5.02123 20.98 3.02123C18.98 1.02123 17.4 1.66123 16.04 3.02123Z"
                  stroke="#FBFBFB"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14.91 4.14844C15.58 6.53844 17.45 8.40844 19.85 9.08844"
                  stroke="#FBFBFB"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Box>
            </Box>
            */}
          </Box>

          {/* User Info */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
            gap: '16px',
            flex: '1 0 0'
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <Typography sx={{
                color: '#121',
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: { xs: '20px', sm: '24px', md: '28px' },
                fontWeight: 700,
                lineHeight: { xs: '26px', sm: '30px', md: '36px' },
                wordBreak: 'break-word'
              }}>
                Hello {user?.first_name || user?.username || 'User'}
              </Typography>
              <Typography sx={{
                color: '#545665',
                fontFamily: 'Plus Jakarta Sans, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: { xs: '14px', md: '16px' },
                fontWeight: 400,
                opacity: 0.75,
                wordBreak: 'break-all',
                display: { xs: 'none', md: 'block' },
              }}>
                {user?.email ? `${user.email}` : ''}
              </Typography>
              <Typography sx={{
                color: '#545665',
                fontFamily: 'Plus Jakarta Sans, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                opacity: 0.75,
                display: { xs: 'block', md: 'none' },
              }}>
                {mobilePhoneLine || 'Add your number below'}
              </Typography>
            </Box>
          </Box>

        </Box>

        {/* Mobile: savings banner under avatar row */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            mt: '12px',
            py: isMobileProfileLayout ? '8px' : '10px',
            px: isMobileProfileLayout ? '16px' : '14px',
            borderRadius: isMobileProfileLayout ? '16px' : '12px',
            bgcolor: isMobileProfileLayout ? '#FFEFE4' : '#FFE8DC',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              color: isMobileProfileLayout ? '#AF734E' : '#B22E4C',
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: isMobileProfileLayout ? '12px' : '13px',
              fontWeight: isMobileProfileLayout ? 500 : 700,
              lineHeight: isMobileProfileLayout ? '16px' : '18px',
              letterSpacing: isMobileProfileLayout ? '0.25px' : 0,
              textAlign: 'center',
            }}
          >
            Users Save ₹3,492 annually on trips with YovoTrip
          </Typography>
        </Box>

          {/* Logout — desktop only (mobile: below Account Setting) */}
          <Button
            onClick={handleLogout}
            sx={{
              display: { xs: 'none', md: 'flex' },
              height: '48px',
              alignItems: 'center',
              gap: '16px',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              textTransform: 'none',
              alignSelf: 'flex-end',
              justifyContent: 'flex-start',
              width: 'auto',
              '&:hover': {
                backgroundColor: '#F5F5F5'
              }
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.90002 7.55828C9.21002 3.95828 11.06 2.48828 15.11 2.48828H15.24C19.71 2.48828 21.5 4.27828 21.5 8.74828V15.2683C21.5 19.7383 19.71 21.5283 15.24 21.5283H15.11C11.09 21.5283 9.24002 20.0783 8.91002 16.5383"
                stroke="#20202A"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15 12H3.62"
                stroke="#20202A"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.85 8.64844L2.5 11.9984L5.85 15.3484"
                stroke="#20202A"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Typography sx={{
              color: '#20202A',
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: '24px'
            }}>
              Log out
            </Typography>
          </Button>
      </Box>
    </Box>

      {/* Account Details Section */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '16px',
        px: { xs: '16px', sm: '20px', md: '104px' },
        pb: { xs: '24px', md: '40px' }
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          width: '100%',
          maxWidth: '1232px'
        }}>
          <Typography sx={{
            color: '#383A47',
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: { xs: isMobileProfileLayout ? '20px' : '22px', sm: '24px', md: '28px' },
            fontWeight: 700,
            lineHeight: { xs: isMobileProfileLayout ? '28px' : '28px', md: '36px' },
            flex: 1,
            minWidth: 0,
          }}>
            Account Details
          </Typography>
          <Button
            onClick={handleSave}
            disabled={loading}
            sx={{
              display: 'flex',
              height: { xs: isMobileProfileLayout ? 32 : 40, md: 48 },
              justifyContent: 'center',
              alignItems: 'center',
              gap: '4px',
              borderRadius: isMobileProfileLayout ? '4px' : '8px',
              backgroundColor: 'black',
              px: { xs: isMobileProfileLayout ? '16px' : '18px', md: '24px' },
              textTransform: 'none',
              flexShrink: 0,
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: '#9a2540'
              },
              '&:disabled': {
                backgroundColor: '#ccc'
              }
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Typography sx={{
                color: '#FBFBFB',
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '24px'
              }}>
                Save
              </Typography>
            )}
          </Button>
        </Box>

        {/* Error and Success Messages */}
        {error && (
          <Alert severity="error" sx={{ width: '100%', maxWidth: '1232px', mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ width: '100%', maxWidth: '1232px', mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Mobile: white card around form (design mock) */}
        <Box sx={{
          width: '100%',
          maxWidth: '1232px',
          bgcolor: { xs: '#FFFFFF', md: 'transparent' },
          borderRadius: { xs: '16px', md: 0 },
          border: { xs: '1px solid #E8E9ED', md: 'none' },
          boxShadow: { xs: '0 1px 6px rgba(32, 32, 42, 0.06)', md: 'none' },
          p: { xs: 2, md: 0 },
        }}>
        {/* Form Fields */}
        <Box sx={{
          display: 'flex',
          width: '100%',
          maxWidth: '1232px',
          flexDirection: 'column',
          gap: { xs: '20px', md: '32px' },
          borderRadius: '16px'
        }}>
          {/* First Row: First Name and Last Name */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: '16px', md: '24px' },
            width: '100%'
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              flex: '1 0 0'
            }}>
              <Typography sx={{
                color: '#20202A',
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '0.2px'
              }}>
                First And Middle Name
              </Typography>
              <TextField
                placeholder="Enter first and middle name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    height: '48px',
                    borderRadius: '8px',
                    backgroundColor: '#F5F5F5',
                    fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: '1px solid #20202A'
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '12px 16px',
                    color: '#20202A',
                    '&::placeholder': {
                      color: '#AEAEB8',
                      opacity: 1
                    }
                  }
                }}
              />
            </Box>

            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              flex: '1 0 0'
            }}>
              <Typography sx={{
                color: '#20202A',
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '0.2px'
              }}>
                Last Name
              </Typography>
              <TextField
                placeholder="Enter last name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    height: '48px',
                    borderRadius: '8px',
                    backgroundColor: '#F5F5F5',
                    fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: '1px solid #20202A'
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '12px 16px',
                    color: '#20202A',
                    '&::placeholder': {
                      color: '#AEAEB8',
                      opacity: 1
                    }
                  }
                }}
              />
            </Box>
          </Box>

          {/* Second Row: Phone Number and Email */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: '16px', md: '24px' },
            width: '100%'
          }}>
            {/* Phone Number */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              flex: '1 0 0'
            }}>
              <Typography sx={{
                color: '#20202A',
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '0.2px'
              }}>
                Phone Number
              </Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
                gap: '10px',
                width: '100%'
              }}>
                {/* Country Code Selector */}
                <FormControl sx={{ minWidth: { xs: 112, sm: 120 }, width: { xs: 'auto', sm: 'auto' }, flexShrink: 0 }}>
                  <Select
                    value={selectedCountry.code}
                    onChange={(event) => {
                      const country = countries.find(c => c.code === event.target.value);
                      setSelectedCountry(country);
                    }}
                    sx={{
                      height: '48px',
                      borderRadius: '8px',
                      backgroundColor: '#F5F5F5',
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #20202A'
                      },
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px',
                        fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#20202A'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '8px',
                          mt: '4px'
                        }
                      }
                    }}
                  >
                    {countries.map((country) => (
                      <MenuItem
                        key={country.code}
                        value={country.code}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                          fontSize: '14px'
                        }}
                      >
                        <span style={{ fontSize: '16px' }}>{country.flag}</span>
                        <span>{country.name}</span>
                        <span style={{ color: '#666', marginLeft: 'auto' }}>{country.code}</span>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Phone Input */}
                <TextField
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      height: '48px',
                      borderRadius: '8px',
                      backgroundColor: '#F5F5F5',
                      fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                      fontSize: '16px',
                      fontWeight: 600,
                      '& fieldset': {
                        border: 'none'
                      },
                      '&:hover fieldset': {
                        border: 'none'
                      },
                      '&.Mui-focused fieldset': {
                        border: '1px solid #20202A'
                      }
                    },
                    '& .MuiInputBase-input': {
                      padding: '12px 16px',
                      color: '#20202A'
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Email */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              flex: '1 0 0'
            }}>
              <Typography sx={{
                color: '#20202A',
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '0.2px'
              }}>
                Registered Email
              </Typography>
              <TextField
                placeholder="Enter email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    height: '48px',
                    borderRadius: '8px',
                    backgroundColor: '#F5F5F5',
                    fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: '1px solid #20202A'
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '12px 16px',
                    color: '#20202A',
                    '&::placeholder': {
                      color: '#AEAEB8',
                      opacity: 1
                    }
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
        </Box>
      </Box>

      {/* Account Setting Section */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '16px',
        px: { xs: '16px', sm: '20px', md: '104px' },
        pb: { xs: '16px', md: '80px' }
      }}>
        <Typography sx={{
          color: '#383A47',
          fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
          fontSize: { xs: isMobileProfileLayout ? '16px' : '22px', sm: '24px', md: '28px' },
          fontWeight: isMobileProfileLayout ? 600 : 700,
          lineHeight: { xs: isMobileProfileLayout ? '24px' : '28px', md: '36px' }
        }}>
          Account Setting
        </Typography>

        <Box sx={{
          display: 'flex',
          padding: { xs: '16px', md: '20px' },
          width: '100%',
          maxWidth: '1232px',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '32px',
          borderRadius: '16px',
          border: '1px solid #E8E9ED',
          bgcolor: { xs: '#FFFFFF', md: '#FFFFFF' },
          boxShadow: { xs: '0 1px 6px rgba(32, 32, 42, 0.06)', md: 'none' },
        }}>
          <Box
            onClick={handleDeleteAccount}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            <Typography sx={{
              color: '#E22E49',
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: { xs: '16px', md: '20px' },
              fontWeight: 700,
              lineHeight: { xs: '22px', md: '28px' }
            }}>
              Delete Account
            </Typography>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.91003 19.9181L15.43 13.3981C16.2 12.6281 16.2 11.3681 15.43 10.5981L8.91003 4.07812"
                stroke="#20202A"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
        </Box>

        {/* Mobile: centered Log out (design mock) */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            justifyContent: 'center',
            width: '100%',
            maxWidth: '1232px',
            mt: '20px',
          }}
        >
          <Button
            onClick={handleLogout}
            sx={{
              display: 'flex',
              height: '48px',
              alignItems: 'center',
              gap: '12px',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.90002 7.55828C9.21002 3.95828 11.06 2.48828 15.11 2.48828H15.24C19.71 2.48828 21.5 4.27828 21.5 8.74828V15.2683C21.5 19.7383 19.71 21.5283 15.24 21.5283H15.11C11.09 21.5283 9.24002 20.0783 8.91002 16.5383"
                stroke="#20202A"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15 12H3.62"
                stroke="#20202A"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.85 8.64844L2.5 11.9984L5.85 15.3484"
                stroke="#20202A"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Typography
              sx={{
                color: '#20202A',
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '24px',
              }}
            >
              Log out
            </Typography>
          </Button>
        </Box>
      </Box>
      </Box>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
          fontSize: '20px',
          fontWeight: 700
        }}>
          Delete Account
        </DialogTitle>
        <DialogContent>
          <Typography sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '16px'
          }}>
            Are you sure you want to delete your account? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteAccount}
            disabled={loading}
            sx={{
              backgroundColor: '#E22E49',
              color: '#FBFBFB',
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#d02642'
              }
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Footer — hidden on small viewports (mobile app-style account screen) */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        width: '100%',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '64px',
        backgroundColor: '#20202A',
        pt: '64px',
        pb: '32px',
        mt: 'auto'
      }}>
        <Box sx={{
          display: 'flex',
          width: '100%',
          maxWidth: '1232px',
          px: { xs: '20px', md: 0 },
          gap: '140px',
          justifyContent: 'space-between',
          flexWrap: { xs: 'wrap', md: 'nowrap' }
        }}>
          {/* YovoTrip Logo and Social */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            <Typography sx={{
              color: '#FBFBFB',
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '28px'
            }}>
              YovoTrip
            </Typography>
            <Box sx={{
              display: 'flex',
              gap: '16px'
            }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M1 9.90181C1 14.3763 4.24975 18.0971 8.5 18.8516V12.3513H6.25V9.85156H8.5V7.85131C8.5 5.60131 9.94975 4.35181 12.0002 4.35181C12.6497 4.35181 13.3503 4.45156 13.9998 4.55131V6.85156H12.85C11.7498 6.85156 11.5 7.40131 11.5 8.10181V9.85156H13.9L13.5002 12.3513H11.5V18.8516C15.7502 18.0971 19 14.3771 19 9.90181C19 4.92406 14.95 0.851562 10 0.851562C5.05 0.851562 1 4.92406 1 9.90181Z" fill="#FBFBFB"/>
              </svg>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.7029 4.11366C19.0071 4.42199 18.2596 4.63033 17.4737 4.72449C18.2846 4.23932 18.8912 3.47572 19.1804 2.57616C18.4186 3.02865 17.5849 3.34716 16.7154 3.51783C16.1307 2.89355 15.3563 2.47977 14.5124 2.34072C13.6684 2.20168 12.8022 2.34515 12.0481 2.74887C11.2941 3.15259 10.6944 3.79396 10.3422 4.57341C9.99003 5.35287 9.90503 6.22679 10.1004 7.05949C8.55682 6.98199 7.04677 6.58079 5.66827 5.88192C4.28977 5.18305 3.07362 4.20213 2.09875 3.00283C1.76542 3.57783 1.57375 4.24449 1.57375 4.95449C1.57338 5.59366 1.73078 6.22303 2.03198 6.78677C2.33319 7.35051 2.76888 7.83119 3.30042 8.18616C2.68398 8.16655 2.08114 7.99998 1.54208 7.70033V7.75033C1.54202 8.64678 1.85211 9.51564 2.41974 10.2095C2.98736 10.9033 3.77756 11.3794 4.65625 11.557C4.0844 11.7118 3.48486 11.7346 2.90292 11.6237C3.15083 12.395 3.63375 13.0695 4.28406 13.5528C4.93437 14.036 5.71951 14.3038 6.52958 14.3187C5.15444 15.3982 3.45616 15.9837 1.70792 15.9812C1.39823 15.9812 1.08881 15.9632 0.78125 15.927C2.55581 17.068 4.62153 17.6735 6.73125 17.6712C13.8729 17.6712 17.7771 11.7562 17.7771 6.62616C17.7771 6.45949 17.7729 6.29116 17.7654 6.12449C18.5248 5.57531 19.1803 4.89524 19.7013 4.11616L19.7029 4.11366Z" fill="#FBFBFB"/>
              </svg>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_1264_12150)">
                  <path d="M19.5838 5.42142C19.4706 5.01745 19.25 4.65171 18.9455 4.36309C18.6324 4.06559 18.2487 3.85279 17.8305 3.74476C16.2655 3.33226 9.99551 3.33226 9.99551 3.33226C7.38162 3.30252 4.76837 3.43332 2.17051 3.72392C1.75234 3.83993 1.36932 4.0575 1.05551 4.35726C0.747181 4.65392 0.523848 5.01976 0.407181 5.42059C0.126925 6.93038 -0.00924233 8.46338 0.000514319 9.99892C-0.00948568 11.5331 0.126348 13.0656 0.407181 14.5773C0.521348 14.9764 0.743848 15.3406 1.05301 15.6348C1.36218 15.9289 1.74718 16.1414 2.17051 16.2539C3.75635 16.6656 9.99551 16.6656 9.99551 16.6656C12.6127 16.6954 15.2293 16.5646 17.8305 16.2739C18.2487 16.1659 18.6324 15.9531 18.9455 15.6556C19.2499 15.367 19.4702 15.0012 19.583 14.5973C19.8706 13.0881 20.0104 11.5544 20.0005 10.0181C20.0221 8.47526 19.8825 6.93439 19.5838 5.42059V5.42142ZM8.00218 12.8523V7.14642L13.2188 9.99976L8.00218 12.8523Z" fill="#FBFBFB"/>
                </g>
              </svg>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.49787 7.51702C7.93707 7.51702 6.66333 8.79076 6.66333 10.3516C6.66333 11.9124 7.93707 13.1861 9.49787 13.1861C11.0587 13.1861 12.3324 11.9124 12.3324 10.3516C12.3324 8.79076 11.0587 7.51702 9.49787 7.51702ZM17.9994 10.3516C17.9994 9.17777 18.01 8.01461 17.9441 6.84294C17.8782 5.48202 17.5677 4.27421 16.5725 3.27903C15.5752 2.28174 14.3695 1.9734 13.0086 1.90748C11.8348 1.84156 10.6717 1.85219 9.5 1.85219C8.32621 1.85219 7.16305 1.84156 5.99138 1.90748C4.63046 1.9734 3.42264 2.28386 2.42747 3.27903C1.43017 4.27633 1.12184 5.48202 1.05592 6.84294C0.99 8.01673 1.00063 9.1799 1.00063 10.3516C1.00063 11.5232 0.99 12.6885 1.05592 13.8602C1.12184 15.2211 1.4323 16.4289 2.42747 17.4241C3.42477 18.4214 4.63046 18.7297 5.99138 18.7956C7.16517 18.8616 8.32833 18.8509 9.5 18.8509C10.6738 18.8509 11.837 18.8616 13.0086 18.7956C14.3695 18.7297 15.5774 18.4193 16.5725 17.4241C17.5698 16.4268 17.8782 15.2211 17.9441 13.8602C18.0121 12.6885 17.9994 11.5254 17.9994 10.3516ZM9.49787 14.7129C7.08437 14.7129 5.13655 12.7651 5.13655 10.3516C5.13655 7.93806 7.08437 5.99024 9.49787 5.99024C11.9114 5.99024 13.8592 7.93806 13.8592 10.3516C13.8592 12.7651 11.9114 14.7129 9.49787 14.7129ZM14.0378 6.83018C13.4743 6.83018 13.0193 6.37513 13.0193 5.81162C13.0193 5.24811 13.4743 4.79306 14.0378 4.79306C14.6013 4.79306 15.0564 5.24811 15.0564 5.81162C15.0565 5.94543 15.0303 6.07795 14.9792 6.20161C14.9281 6.32526 14.853 6.43761 14.7584 6.53223C14.6638 6.62684 14.5515 6.70186 14.4278 6.75299C14.3041 6.80412 14.1716 6.83035 14.0378 6.83018Z" fill="#FBFBFB"/>
              </svg>
            </Box>
          </Box>

          {/* Trending Trips */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <Typography sx={{
              color: '#FBFBFB',
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '28px'
            }}>
              Trending Trips
            </Typography>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {['Canada', 'Alaksa', 'France', 'Iceland'].map((item) => (
                <Typography
                  key={item}
                  sx={{
                    color: '#FBFBFB',
                    fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '24px',
                    opacity: 0.7,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* Visa Free Destinations */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <Typography sx={{
              color: '#FBFBFB',
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '28px'
            }}>
              Visa Free Destinations
            </Typography>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {['Northern Lights', 'Cruising & sailing', 'Multi-activities', 'Kayaing'].map((item) => (
                <Typography
                  key={item}
                  sx={{
                    color: '#FBFBFB',
                    fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '24px',
                    opacity: 0.7,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* International Destinations */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <Typography sx={{
              color: '#FBFBFB',
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '28px'
            }}>
              International Destinations
            </Typography>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {['Bali Travel Guide', 'Sri Lanka Travel Guide', 'Peru Travel Guide', 'Bali Travel Guide'].map((item, index) => (
                <Typography
                  key={`${item}-${index}`}
                  sx={{
                    color: '#FBFBFB',
                    fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '24px',
                    opacity: 0.7,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* Contact Us */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <Typography sx={{
              color: '#FBFBFB',
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '28px'
            }}>
              Contact Us
            </Typography>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {['Our Story', 'Work with us'].map((item) => (
                <Typography
                  key={item}
                  sx={{
                    color: '#FBFBFB',
                    fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '24px',
                    opacity: 0.7,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>

      <RegionalSettingsModal
        open={regionalSettingsOpen}
        onClose={() => setRegionalSettingsOpen(false)}
        onSave={() => {
          if (window.flightCache) window.flightCache.clear();
        }}
      />

      {mobileBottomNav}
      {mobileChatbotOverlay}
    </>
  );
};

export default AccountPage;
